import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Chip,
  Alert,
  Snackbar,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ShieldIcon from '@mui/icons-material/Shield';
import RefreshIcon from '@mui/icons-material/Refresh';
import BlockIcon from '@mui/icons-material/Block';
import { Trade, TradeType, Client } from '../types';
import { tradeService } from '../services/trade.service';
import { clientService } from '../services/client.service';
import {
  applyLIFO,
  computeBreakevenForPosition,
  BreakevenResult,
  TradeLeg,
  calculateChargesBreakdown,
  DEFAULT_OPTION_BROKERAGE,
} from '../hooks/useTradeCalculations';
import Loading from '../components/common/Loading';
import { istDay, getTodayDate } from '../utils/formatters';
import { getFuturesSLAmount } from '../utils/stopLoss';

// ============================================================================
// COLORS (matched from TradingDashboard)
// ============================================================================

const COLORS = {
  primary: '#0A0A0A',
  primaryLight: '#525252',
  positive: '#10B981',
  positiveLight: '#D1FAE5',
  negativeDark: '#DC2626',
  negative: '#EF4444',
  negativeLight: '#FEE2E2',
  warning: '#F59E0B',
  warningLight: '#FEF3C7',
  blue: '#3B82F6',
  blueLight: '#DBEAFE',
  pageBg: '#F8F7FF',
  cardBg: '#FFFFFF',
  textPrimary: '#1E1B4B',
  textSecondary: '#6B7280',
  border: '#E5E7EB',
};

// ============================================================================
// FORMATTERS
// ============================================================================

const fmtCurrency = (val: number): string =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val);

const fmtCurrencyFull = (val: number): string =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val);

// ============================================================================
// HELPERS — copied from TradingDashboard (pure functions, formulas unchanged)
// ============================================================================

function isEquityTradeType(t: TradeType): boolean {
  return t === TradeType.STOCK || t === TradeType.EQUITY || t === TradeType.INTRA_STOCK;
}
function isEquityType(t: string): boolean {
  return t === 'Stock' || t === 'Equity' || t === 'Intra Stock';
}
function normalizeTradeType(t: TradeType): string {
  if (t === TradeType.STOCK || t === TradeType.EQUITY || t === TradeType.INTRA_STOCK) return 'equity';
  if (t === TradeType.FUTURE) return 'future';
  return 'option';
}

// getFuturesSLAmount extracted to utils/stopLoss.ts (shared with TradingDashboard).

function isTradeFromToday(t: Trade): boolean {
  // IST-shift the trade timestamp so trades opened after 18:30 UTC don't get
  // mis-bucketed as the previous IST day.
  return istDay(t.date || t.createdAt || '') === getTodayDate();
}

function getTradeTypeLabel(tradeType: TradeType, tradeLegs: Trade[]): string {
  if (tradeType === TradeType.FUTURE) return 'Future';
  if (tradeType === TradeType.OPTION) return 'Option';
  const hasIntra = tradeLegs.some(t => t.tradeType === TradeType.INTRA_STOCK || isTradeFromToday(t));
  return hasIntra ? 'Intraday' : 'Delivery';
}

function selectPositionBreakeven(be: BreakevenResult, tradeType: TradeType, tradeLegs: Trade[]): number {
  if (tradeType === TradeType.FUTURE) return be.futureBE ?? 0;
  if (tradeType === TradeType.OPTION) return be.optionBE ?? 0;
  const hasIntra = tradeLegs.some(t => t.tradeType === TradeType.INTRA_STOCK || isTradeFromToday(t));
  if (hasIntra) return be.intradayBE ?? be.fullClearBE ?? 0;
  return be.carryForwardBE ?? be.deliveryBE ?? be.fullClearBE ?? 0;
}

// ============================================================================
// MERGED POSITION TYPE
// ============================================================================

interface MergedPosition {
  key: string;
  clientId: string;
  clientName: string;
  scrip: string;
  tradeType: TradeType;
  direction: 'BUY' | 'SELL';
  balanceLots: number;
  lotSize: number;
  shares: number;
  weightedAvgBuyPrice: number;
  investedAmount: number;
  currentValue: number;
  currentPrice: number;
  unrealizedPnL: number;
  realizedPnL: number;
  breakeven: BreakevenResult;
  tradeLegs: Trade[];
  deployedAmount: number;
  isMTF: boolean;
  leverage?: string;
}

// ============================================================================
// POSITION SL INFO
// ============================================================================

interface PositionSLInfo {
  beRate: number;
  slAmount: number;
  slRate: number;
  chargeInclusivePnL: number;
  slProgressPct: number;
  isFuture: boolean;
}

function computePositionSL(p: MergedPosition, clientMap: Map<string, Client>): PositionSLInfo | null {
  const deployed = p.deployedAmount;
  if (!deployed || deployed <= 0) return null;
  if (!p.shares || p.shares <= 0) return null;

  const beRate = selectPositionBreakeven(p.breakeven, p.tradeType, p.tradeLegs);
  if (!beRate || beRate <= 0) return null;

  const chargeInclusivePnL = p.direction === 'SELL'
    ? (beRate - p.currentPrice) * p.shares
    : (p.currentPrice - beRate) * p.shares;

  const isFuture = p.tradeType === TradeType.FUTURE;
  const slAmount = isFuture ? getFuturesSLAmount(p.balanceLots) : deployed * 0.02;
  if (!slAmount || slAmount <= 0) return null;

  const client = clientMap.get(p.clientId);
  const brokerageRates = client?.brokerageRates || { stock: 0.003, option: 100, intraStock: 0.0003 };
  const isIntra = p.tradeLegs.some(t => t.tradeType === TradeType.INTRA_STOCK || isTradeFromToday(t));
  const exitBrokRate = p.tradeType === TradeType.FUTURE
    ? (client?.brokerageRates?.future || 0.0003)
    : isIntra ? (brokerageRates.intraStock || 0.0003) : (brokerageRates.stock || 0.003);
  const exitTradeType = p.tradeType === TradeType.FUTURE ? TradeType.FUTURE
    : p.tradeType === TradeType.OPTION ? TradeType.OPTION
    : isIntra ? TradeType.INTRA_STOCK : TradeType.EQUITY;
  const fixedBrokerage = p.tradeType === TradeType.OPTION
    ? (client?.brokerageRates?.option || DEFAULT_OPTION_BROKERAGE) * p.balanceLots
    : undefined;

  let slRate: number;
  if (p.direction === 'BUY') {
    slRate = beRate - slAmount / p.shares;
    for (let i = 0; i < 15; i++) {
      const exitValue = slRate * p.shares;
      const exitCharges = calculateChargesBreakdown(exitTradeType, 0, exitValue, exitBrokRate, isIntra, 'NSE', fixedBrokerage);
      const newSlRate = beRate - (slAmount - exitCharges.totalCharges) / p.shares;
      if (Math.abs(newSlRate - slRate) < 0.001) { slRate = newSlRate; break; }
      slRate = newSlRate;
    }
  } else {
    slRate = beRate + slAmount / p.shares;
    for (let i = 0; i < 15; i++) {
      const exitValue = slRate * p.shares;
      const exitCharges = calculateChargesBreakdown(exitTradeType, exitValue, 0, exitBrokRate, isIntra, 'NSE', fixedBrokerage);
      const newSlRate = beRate + (slAmount - exitCharges.totalCharges) / p.shares;
      if (Math.abs(newSlRate - slRate) < 0.001) { slRate = newSlRate; break; }
      slRate = newSlRate;
    }
  }

  const slProgressPct = chargeInclusivePnL >= 0 ? 0 : Math.min(999, (Math.abs(chargeInclusivePnL) / slAmount) * 100);

  return {
    beRate: Math.round(beRate * 100) / 100,
    slAmount: Math.round(slAmount),
    slRate: Math.round(slRate * 100) / 100,
    chargeInclusivePnL: Math.round(chargeInclusivePnL),
    slProgressPct: Math.round(slProgressPct * 10) / 10,
    isFuture,
  };
}

// ============================================================================
// MERGE OPEN POSITIONS (same as TradingDashboard)
// ============================================================================

function mergeOpenPositions(trades: Trade[], clients: Client[]): MergedPosition[] {
  const clientMap = new Map(clients.map(c => [c.clientId, c]));
  const openTrades = trades.filter(t => t.status === 'OPEN' && !t.isDeleted);

  const groups = new Map<string, Trade[]>();
  for (const t of openTrades) {
    const normType = normalizeTradeType(t.tradeType);
    const dir = t.direction || 'BUY';
    const key = `${t.clientId}__${t.scrip}__${normType}__${dir}`;
    const group = groups.get(key) || [];
    group.push(t);
    groups.set(key, group);
  }

  const merged: MergedPosition[] = [];

  for (const [key, groupTrades] of groups) {
    const first = groupTrades[0];
    const client = clientMap.get(first.clientId);
    const brokerageRates = client?.brokerageRates || { stock: 0.003, option: 100, intraStock: 0.0003 };
    const direction: 'BUY' | 'SELL' = (first.direction as 'BUY' | 'SELL') || 'BUY';
    const isSellFirst = direction === 'SELL';

    const legs: TradeLeg[] = groupTrades.map(t => ({
      tradeId: t.tradeId,
      lots: t.purchaseLots,
      lotSize: t.lotSize,
      price: t.purchasePrice,
      date: t.date || t.createdAt,
      tradeType: t.tradeType,
      saleLots: t.saleLots,
      salePrice: t.salePrice,
      direction: t.direction as 'BUY' | 'SELL' | undefined,
    }));

    const totalSoldShares = groupTrades.reduce((sum, t) => sum + (t.saleLots || 0) * (t.lotSize || 1), 0);
    const lifo = applyLIFO(legs, totalSoldShares);
    if (lifo.totalRemainingShares <= 0) continue;

    const latestTrade = [...groupTrades].sort((a, b) => new Date(b.createdAt || b.date).getTime() - new Date(a.createdAt || a.date).getTime())[0];
    const cmp = latestTrade.currentPrice || latestTrade.purchasePrice;

    const invested = lifo.totalRemainingCost;
    const currentValue = cmp * lifo.totalRemainingShares;
    const unrealizedPnL = isSellFirst ? (invested - currentValue) : (currentValue - invested);
    const realizedPnL = groupTrades.reduce((sum, t) => sum + (t.realizedPnL || 0), 0);

    const primaryType = isEquityTradeType(first.tradeType) ? TradeType.EQUITY : first.tradeType;
    const breakeven = computeBreakevenForPosition(lifo.remainingLegs, brokerageRates, primaryType, client?.name, direction);

    const lotSize = first.lotSize || 1;
    const balanceLots = isEquityTradeType(first.tradeType) ? lifo.totalRemainingShares : Math.round(lifo.totalRemainingShares / lotSize);

    merged.push({
      key,
      clientId: first.clientId,
      clientName: first.clientName,
      scrip: first.scrip,
      tradeType: primaryType,
      direction,
      balanceLots,
      lotSize,
      shares: lifo.totalRemainingShares,
      weightedAvgBuyPrice: lifo.weightedAvgBuyPrice,
      investedAmount: invested,
      currentValue,
      currentPrice: cmp,
      unrealizedPnL,
      realizedPnL,
      breakeven,
      tradeLegs: groupTrades,
      deployedAmount: (() => {
        const sorted = [...groupTrades].sort((a, b) => new Date(a.date || a.createdAt).getTime() - new Date(b.date || b.createdAt).getTime());
        const fromTrade = sorted[0]?.deployedAmount;
        if (fromTrade && fromTrade > 0) return fromTrade;
        return client?.currentDeployedAmount ?? client?.initialCapital ?? 0;
      })(),
      isMTF: groupTrades.some(t => t.isMTF),
      leverage: groupTrades.find(t => t.isMTF)?.leverage,
    });
  }

  return merged.sort((a, b) => b.investedAmount - a.investedAmount);
}

// ============================================================================
// ENRICHED POSITION (position + SL info for display)
// ============================================================================

interface EnrichedPosition {
  pos: MergedPosition;
  sl: PositionSLInfo;
  typeLabel: string;
  lossPercent: number; // |loss| / deployed * 100
}

// ============================================================================
// ULTIMATE CLOSURE CLIENT
// ============================================================================

interface UltimateClosureClient {
  clientId: string;
  clientName: string;
  availableCash: number;
  threshold: number;
  totalLoss: number;
  lossPercent: number;
  equityLoss: number;
  equityPositions: number;
  futuresLoss: number;
  futuresPositions: number;
  futuresLots: number;
  optionsLoss: number;
  optionsPositions: number;
  scrips: string[];
  positions: MergedPosition[];
}

// ============================================================================
// SECTION HEADER
// ============================================================================

function SectionHeader({ title, subtitle, color, count }: { title: string; subtitle: string; color: string; count: number }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
      <Box sx={{ width: 4, height: 36, borderRadius: 2, bgcolor: color }} />
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: COLORS.textPrimary }}>{title}</Typography>
          <Chip label={count} size="small" sx={{ fontWeight: 700, fontSize: '0.75rem', bgcolor: `${color}18`, color, height: 24 }} />
        </Box>
        <Typography variant="body2" sx={{ color: COLORS.textSecondary, fontSize: '0.75rem' }}>{subtitle}</Typography>
      </Box>
    </Box>
  );
}

// ============================================================================
// POSITION TABLE (shared between Urgent + Warning sections)
// ============================================================================

const COL_HEADERS = [
  { l: 'Client', a: 'left' as const },
  { l: 'Scrip', a: 'left' as const },
  { l: 'Type', a: 'left' as const },
  { l: 'Dir', a: 'center' as const },
  { l: 'Lots', a: 'right' as const },
  { l: 'Shares', a: 'right' as const },
  { l: 'Avg Price', a: 'right' as const },
  { l: 'CMP', a: 'right' as const },
  { l: 'BE Rate', a: 'right' as const },
  { l: 'SL Rate', a: 'right' as const },
  { l: 'SL Amount', a: 'right' as const },
  { l: 'SL %', a: 'right' as const },
  { l: 'Deployed', a: 'right' as const },
  { l: 'Unreal Loss', a: 'right' as const },
  { l: 'Loss %', a: 'right' as const },
];

function PositionTable({ items, headerBg, rowBgFn }: {
  items: EnrichedPosition[];
  headerBg: string;
  rowBgFn: (ep: EnrichedPosition) => string;
}) {
  if (items.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">No positions in this category</Typography>
      </Box>
    );
  }
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, border: `1px solid ${COLORS.border}` }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {COL_HEADERS.map(h => (
              <TableCell
                key={h.l}
                align={h.a}
                sx={{
                  bgcolor: headerBg,
                  fontWeight: 700,
                  fontSize: '0.6rem',
                  color: COLORS.textSecondary,
                  textTransform: 'uppercase',
                  py: 0.8,
                  px: 1,
                  borderBottom: `2px solid ${COLORS.border}`,
                  whiteSpace: 'nowrap',
                }}
              >
                {h.l}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map(ep => {
            const { pos: p, sl, typeLabel, lossPercent } = ep;
            const isOption = p.tradeType === TradeType.OPTION;
            const isFutOrOpt = sl.isFuture || isOption;
            // Parse Call/Put from scrip name for options
            const isCall = isOption && p.scrip.toUpperCase().includes(' CE');
            const isPut = isOption && p.scrip.toUpperCase().includes(' PE');

            return (
              <TableRow key={p.key} sx={{ bgcolor: rowBgFn(ep), '&:hover': { bgcolor: `${COLORS.border}40` } }}>
                {/* Client */}
                <TableCell sx={{ px: 1, py: 0.8 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{p.clientName}</Typography>
                    {p.isMTF && (
                      <Typography component="span" sx={{ fontSize: '0.5rem', fontWeight: 700, color: '#92400E', bgcolor: '#FEF3C7', px: 0.3, py: 0.1, borderRadius: 0.5 }}>
                        MTF{p.leverage ? ` ${p.leverage}` : ''}
                      </Typography>
                    )}
                  </Box>
                </TableCell>

                {/* Scrip */}
                <TableCell sx={{ px: 1, py: 0.8 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700 }}>{p.scrip}</Typography>
                    {isOption && (isCall || isPut) && (
                      <Chip
                        label={isCall ? 'CE' : 'PE'}
                        size="small"
                        sx={{
                          fontSize: '0.45rem', height: 16, fontWeight: 700,
                          bgcolor: isCall ? '#D1FAE5' : '#FEE2E2',
                          color: isCall ? '#065F46' : '#991B1B',
                        }}
                      />
                    )}
                  </Box>
                </TableCell>

                {/* Type */}
                <TableCell sx={{ px: 1, py: 0.8 }}>
                  <Chip
                    label={typeLabel}
                    size="small"
                    sx={{
                      fontSize: '0.5rem', height: 18, fontWeight: 600,
                      bgcolor: typeLabel === 'Intraday' ? '#D1FAE5' : typeLabel === 'Future' ? '#DBEAFE' : typeLabel === 'Option' ? '#EDE9FE' : '#E0E7FF',
                      color: typeLabel === 'Intraday' ? '#065F46' : typeLabel === 'Future' ? '#1D4ED8' : typeLabel === 'Option' ? '#6D28D9' : '#3730A3',
                    }}
                  />
                </TableCell>

                {/* Direction */}
                <TableCell align="center" sx={{ px: 1, py: 0.8 }}>
                  <Chip
                    label={p.direction}
                    size="small"
                    sx={{
                      fontSize: '0.5rem', height: 18, fontWeight: 700,
                      bgcolor: p.direction === 'BUY' ? '#D1FAE5' : '#FEE2E2',
                      color: p.direction === 'BUY' ? COLORS.positive : COLORS.negative,
                    }}
                  />
                </TableCell>

                {/* Lots */}
                <TableCell align="right" sx={{ px: 1, py: 0.8, fontSize: '0.75rem', fontWeight: 600 }}>
                  {isFutOrOpt ? p.balanceLots : '-'}
                </TableCell>

                {/* Shares */}
                <TableCell align="right" sx={{ px: 1, py: 0.8, fontSize: '0.75rem', fontWeight: 600 }}>
                  {p.shares.toLocaleString('en-IN')}
                </TableCell>

                {/* Avg Price */}
                <TableCell align="right" sx={{ px: 1, py: 0.8, fontSize: '0.75rem', fontWeight: 600 }}>
                  {fmtCurrencyFull(p.weightedAvgBuyPrice)}
                </TableCell>

                {/* CMP */}
                <TableCell align="right" sx={{ px: 1, py: 0.8, fontSize: '0.75rem', fontWeight: 700, color: COLORS.blue }}>
                  {fmtCurrencyFull(p.currentPrice)}
                </TableCell>

                {/* BE Rate */}
                <TableCell align="right" sx={{ px: 1, py: 0.8, fontSize: '0.75rem', fontWeight: 700, color: COLORS.primary }}>
                  {fmtCurrencyFull(sl.beRate)}
                </TableCell>

                {/* SL Rate */}
                <TableCell align="right" sx={{ px: 1, py: 0.8, fontSize: '0.75rem', fontWeight: 700, color: COLORS.negative }}>
                  {fmtCurrencyFull(sl.slRate)}
                </TableCell>

                {/* SL Amount */}
                <TableCell align="right" sx={{ px: 1, py: 0.8 }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: COLORS.warning }}>{fmtCurrency(sl.slAmount)}</Typography>
                  <Typography sx={{ fontSize: '0.45rem', color: COLORS.textSecondary }}>
                    {sl.isFuture ? `${p.balanceLots}L sched` : '2% deployed'}
                  </Typography>
                </TableCell>

                {/* SL % (progress) */}
                <TableCell align="right" sx={{ px: 1, py: 0.8 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(sl.slProgressPct, 100)}
                      sx={{
                        flex: 1, height: 6, borderRadius: 3, bgcolor: '#E5E7EB',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: sl.slProgressPct >= 100 ? COLORS.negative : sl.slProgressPct >= 50 ? COLORS.warning : COLORS.positive,
                          borderRadius: 3,
                        },
                      }}
                    />
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, minWidth: 36, textAlign: 'right', color: sl.slProgressPct >= 100 ? COLORS.negative : sl.slProgressPct >= 50 ? COLORS.warning : COLORS.textSecondary }}>
                      {sl.slProgressPct.toFixed(0)}%
                    </Typography>
                  </Box>
                </TableCell>

                {/* Deployed */}
                <TableCell align="right" sx={{ px: 1, py: 0.8, fontSize: '0.75rem', fontWeight: 600 }}>
                  {fmtCurrency(p.deployedAmount)}
                </TableCell>

                {/* Unrealised Loss */}
                <TableCell align="right" sx={{ px: 1, py: 0.8, fontWeight: 700, color: COLORS.negative, fontSize: '0.8rem' }}>
                  {fmtCurrency(sl.chargeInclusivePnL)}
                </TableCell>

                {/* Loss % */}
                <TableCell align="right" sx={{ px: 1, py: 0.8 }}>
                  <Chip
                    label={`${lossPercent.toFixed(2)}%`}
                    size="small"
                    sx={{
                      fontWeight: 700, fontSize: '0.6rem', height: 20,
                      bgcolor: lossPercent >= 5 ? COLORS.negative : lossPercent >= 3 ? '#F59E0B' : '#FB923C',
                      color: '#fff',
                    }}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function RiskDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [clients, setClients] = useState<Client[]>([]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [t, c] = await Promise.all([tradeService.getTrades(), clientService.getClients()]);
      setTrades(t);
      setClients(c);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Merge open positions
  const positions = useMemo(() => mergeOpenPositions(trades, clients), [trades, clients]);

  // Compute SL for each position and bucket
  const { urgentPositions, warningPositions, allWithSL } = useMemo(() => {
    const clientMap = new Map(clients.map(c => [c.clientId, c]));
    const urgent: EnrichedPosition[] = [];
    const warning: EnrichedPosition[] = [];
    const all: EnrichedPosition[] = [];

    for (const p of positions) {
      const sl = computePositionSL(p, clientMap);
      if (!sl) continue;
      if (sl.chargeInclusivePnL >= 0) continue; // no loss — skip

      const lossAbs = Math.abs(sl.chargeInclusivePnL);
      const lossPercent = p.deployedAmount > 0 ? (lossAbs / p.deployedAmount) * 100 : 0;
      const typeLabel = getTradeTypeLabel(p.tradeType, p.tradeLegs);

      const ep: EnrichedPosition = { pos: p, sl, typeLabel, lossPercent: Math.round(lossPercent * 100) / 100 };
      all.push(ep);

      if (sl.slProgressPct >= 100) {
        urgent.push(ep);
      } else if (sl.slProgressPct >= 50) {
        warning.push(ep);
      }
    }

    urgent.sort((a, b) => b.sl.slProgressPct - a.sl.slProgressPct);
    warning.sort((a, b) => b.sl.slProgressPct - a.sl.slProgressPct);

    return { urgentPositions: urgent, warningPositions: warning, allWithSL: all };
  }, [positions, clients]);

  // Ultimate closure (12% daily loss)
  const ultimateClients = useMemo(() => {
    const clientMap = new Map(clients.map(c => [c.clientId, c]));
    const clientPositions = new Map<string, MergedPosition[]>();
    for (const p of positions) {
      const arr = clientPositions.get(p.clientId) || [];
      arr.push(p);
      clientPositions.set(p.clientId, arr);
    }

    const results: UltimateClosureClient[] = [];

    for (const [clientId, posList] of clientPositions) {
      const client = clientMap.get(clientId);
      if (!client) continue;

      // availableCash here is the loss-absorption buffer. Prefer the
      // start-of-day snapshot (fresh each morning). Fallback mirrors the
      // backend snapshot formula in clients.handler.ts:340-354 so the two
      // sources agree: initialCapital + ΣrealizedPnL − Σ open invested.
      let availableCash = client.startOfDayAvailableCash;
      if (!availableCash || availableCash <= 0) {
        const allRealizedPnL = posList.reduce((s, p) => s + p.realizedPnL, 0);
        const openInvested = posList
          .filter(p => p.balanceLots > 0)
          .reduce((s, p) => s + p.investedAmount, 0);
        availableCash = client.initialCapital + allRealizedPnL - openInvested;
      }
      if (availableCash <= 0) continue;

      const totalLoss = posList.reduce((sum, p) => sum + Math.min(p.unrealizedPnL, 0), 0);
      if (totalLoss >= 0) continue;

      const threshold = availableCash * 0.12;
      const lossAbs = Math.abs(totalLoss);
      const lossPercent = (lossAbs / availableCash) * 100;

      if (lossAbs < threshold) continue;

      const eqPositions = posList.filter(p => isEquityType(p.tradeType));
      const futPositions = posList.filter(p => p.tradeType === TradeType.FUTURE);
      const optPositions = posList.filter(p => p.tradeType === TradeType.OPTION);

      results.push({
        clientId,
        clientName: client.name,
        availableCash,
        threshold: Math.round(threshold),
        totalLoss: Math.round(totalLoss),
        lossPercent: Math.round(lossPercent * 100) / 100,
        equityLoss: Math.round(eqPositions.reduce((s, p) => s + Math.min(p.unrealizedPnL, 0), 0)),
        equityPositions: eqPositions.filter(p => p.unrealizedPnL < 0).length,
        futuresLoss: Math.round(futPositions.reduce((s, p) => s + Math.min(p.unrealizedPnL, 0), 0)),
        futuresPositions: futPositions.filter(p => p.unrealizedPnL < 0).length,
        futuresLots: futPositions.reduce((s, p) => s + p.balanceLots, 0),
        optionsLoss: Math.round(optPositions.reduce((s, p) => s + Math.min(p.unrealizedPnL, 0), 0)),
        optionsPositions: optPositions.filter(p => p.unrealizedPnL < 0).length,
        scrips: posList.filter(p => p.unrealizedPnL < 0).map(p => p.scrip),
        positions: posList,
      });
    }

    return results.sort((a, b) => b.lossPercent - a.lossPercent);
  }, [positions, clients]);

  // Summary counts
  const totalPositions = positions.length;
  const atRiskCount = allWithSL.filter(ep => ep.sl.slProgressPct >= 50).length;
  const urgentCount = urgentPositions.length;
  const ultimateCount = ultimateClients.length;

  // ---- Render ----

  if (loading && trades.length === 0) {
    return <Loading message="Loading risk data..." />;
  }

  return (
    <Box sx={{ bgcolor: COLORS.pageBg, minHeight: '100vh', pb: 3 }}>
      {/* Error Snackbar */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: COLORS.textPrimary }}>
            Risk Dashboard
          </Typography>
          <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
            Stop-loss tracking, urgent closure alerts, and account-level risk monitoring. Auto-refreshes every 30s.
          </Typography>
        </Box>
        <Tooltip title="Refresh now">
          <IconButton onClick={fetchData} sx={{ bgcolor: `${COLORS.primary}10`, '&:hover': { bgcolor: `${COLORS.primary}20` } }}>
            <RefreshIcon sx={{ color: COLORS.primary }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Loading indicator for background refresh */}
      {loading && trades.length > 0 && <LinearProgress sx={{ mb: 1, borderRadius: 2 }} />}

      {/* ============================================================ */}
      {/* TOP: SUMMARY CARDS                                           */}
      {/* ============================================================ */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {/* Total Positions */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, border: `1px solid ${COLORS.border}`, boxShadow: 'none' }}>
            <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ShieldIcon sx={{ color: COLORS.blue, fontSize: 20 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.textSecondary, fontSize: '0.75rem' }}>Total Positions</Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 900, color: COLORS.textPrimary }}>{totalPositions}</Typography>
              <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>Open positions across all clients</Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Positions at Risk (SL >= 50%) */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, border: `1px solid ${COLORS.warning}40`, bgcolor: '#FFFBEB', boxShadow: 'none' }}>
            <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <WarningAmberIcon sx={{ color: COLORS.warning, fontSize: 20 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#92400E', fontSize: '0.75rem' }}>Positions at Risk</Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 900, color: '#92400E' }}>{atRiskCount}</Typography>
              <Typography variant="caption" sx={{ color: '#B45309' }}>SL progress &gt;= 50%</Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Urgent Closures (SL >= 100%) */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, border: `1px solid ${COLORS.negative}40`, bgcolor: '#FEF2F2', boxShadow: 'none' }}>
            <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ErrorOutlineIcon sx={{ color: COLORS.negative, fontSize: 20 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.negativeDark, fontSize: '0.75rem' }}>Urgent Closures</Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 900, color: COLORS.negativeDark }}>{urgentCount}</Typography>
              <Typography variant="caption" sx={{ color: COLORS.negative }}>SL breached — close immediately</Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Ultimate Closures (12% account loss) */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            borderRadius: 2,
            border: ultimateCount > 0 ? '2px solid #991B1B' : `1px solid ${COLORS.border}`,
            bgcolor: ultimateCount > 0 ? '#7F1D1D' : COLORS.cardBg,
            boxShadow: 'none',
            ...(ultimateCount > 0 && {
              animation: 'pulseRed 2s infinite',
              '@keyframes pulseRed': {
                '0%, 100%': { boxShadow: '0 0 5px rgba(220, 38, 38, 0.3)' },
                '50%': { boxShadow: '0 0 15px rgba(220, 38, 38, 0.6)' },
              },
            }),
          }}>
            <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <BlockIcon sx={{ color: ultimateCount > 0 ? '#FCA5A5' : COLORS.textSecondary, fontSize: 20 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, color: ultimateCount > 0 ? '#FCA5A5' : COLORS.textSecondary, fontSize: '0.75rem' }}>
                  Ultimate Closures
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 900, color: ultimateCount > 0 ? '#FEF08A' : COLORS.textPrimary }}>{ultimateCount}</Typography>
              <Typography variant="caption" sx={{ color: ultimateCount > 0 ? '#FCA5A5' : COLORS.textSecondary }}>
                Clients with &gt;12% daily loss
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ============================================================ */}
      {/* SECTION 1: URGENT CLOSURE POSITIONS (SL >= 100%)             */}
      {/* ============================================================ */}
      <Box sx={{ mb: 4 }}>
        <SectionHeader
          title="Urgent Closure Positions"
          subtitle="SL breached (>= 100%) -- positions that must be closed immediately"
          color={COLORS.negative}
          count={urgentCount}
        />
        <PositionTable
          items={urgentPositions}
          headerBg="#FEF2F2"
          rowBgFn={(ep) => ep.lossPercent >= 5 ? '#FEF2F2' : ep.lossPercent >= 3 ? '#FFFBEB' : '#fff'}
        />
      </Box>

      {/* ============================================================ */}
      {/* SECTION 2: WARNING ZONE (SL 50% - 99%)                      */}
      {/* ============================================================ */}
      <Box sx={{ mb: 4 }}>
        <SectionHeader
          title="Warning Zone"
          subtitle="SL progress 50-99% -- approaching stop loss, monitor closely"
          color={COLORS.warning}
          count={warningPositions.length}
        />
        <PositionTable
          items={warningPositions}
          headerBg="#FFFBEB"
          rowBgFn={(ep) => ep.sl.slProgressPct >= 80 ? '#FFF7ED' : '#fff'}
        />
      </Box>

      {/* ============================================================ */}
      {/* SECTION 3: ULTIMATE CLOSURE ALERTS (12% account loss)        */}
      {/* ============================================================ */}
      <Box sx={{ mb: 4 }}>
        <SectionHeader
          title="Ultimate Closure Alerts"
          subtitle="Clients whose total unrealised loss exceeds 12% of start-of-day available cash"
          color="#991B1B"
          count={ultimateCount}
        />

        {ultimateClients.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">No accounts have breached the 12% daily loss threshold</Typography>
          </Box>
        ) : (
          ultimateClients.map(uc => (
            <Box key={uc.clientId} sx={{ mb: 3 }}>
              {/* Alert banner */}
              <Box sx={{
                background: 'linear-gradient(135deg, #991B1B 0%, #B91C1C 100%)',
                borderRadius: '12px 12px 0 0',
                px: 2.5,
                py: 1.5,
                border: '2px solid #FCA5A5',
                borderBottom: 'none',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningAmberIcon sx={{ color: '#FEF08A', fontSize: 22 }} />
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontWeight: 900, color: '#fff', fontSize: '0.95rem' }}>{uc.clientName}</Typography>
                        <Typography sx={{ fontWeight: 800, color: '#FEF08A', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>ACCOUNT BLOCKED</Typography>
                      </Box>
                      <Typography sx={{ fontSize: '0.65rem', color: '#FCA5A5' }}>
                        12% daily loss limit breached -- close all positions, no new trades
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography sx={{ fontSize: '0.5rem', color: '#FCA5A5', textTransform: 'uppercase' }}>Start-of-Day Cash</Typography>
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#fff' }}>{fmtCurrency(uc.availableCash)}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography sx={{ fontSize: '0.5rem', color: '#FCA5A5', textTransform: 'uppercase' }}>12% Limit</Typography>
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#FEF08A' }}>{fmtCurrency(uc.threshold)}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography sx={{ fontSize: '0.5rem', color: '#FCA5A5', textTransform: 'uppercase' }}>Total Loss</Typography>
                      <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: '#FEF08A' }}>{fmtCurrency(uc.totalLoss)}</Typography>
                    </Box>
                    {uc.equityLoss < 0 && (
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography sx={{ fontSize: '0.5rem', color: '#FCA5A5', textTransform: 'uppercase' }}>Equity</Typography>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#fff' }}>{fmtCurrency(uc.equityLoss)} ({uc.equityPositions})</Typography>
                      </Box>
                    )}
                    {uc.futuresLoss < 0 && (
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography sx={{ fontSize: '0.5rem', color: '#FCA5A5', textTransform: 'uppercase' }}>Futures</Typography>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#fff' }}>{fmtCurrency(uc.futuresLoss)} ({uc.futuresLots}L)</Typography>
                      </Box>
                    )}
                    {uc.optionsLoss < 0 && (
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography sx={{ fontSize: '0.5rem', color: '#FCA5A5', textTransform: 'uppercase' }}>Options</Typography>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#fff' }}>{fmtCurrency(uc.optionsLoss)} ({uc.optionsPositions})</Typography>
                      </Box>
                    )}
                    <Chip label={`${uc.lossPercent.toFixed(1)}%`} sx={{ fontWeight: 900, fontSize: '0.8rem', height: 26, bgcolor: '#FEF08A', color: '#991B1B' }} />
                  </Box>
                </Box>
              </Box>

              {/* Position breakdown table for this client */}
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '0 0 12px 12px', border: '2px solid #FCA5A5', borderTop: 'none' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Scrip', 'Type', 'Dir', 'Lots', 'Shares', 'Avg Price', 'CMP', 'Invested', 'Current Value', 'Unrealised P&L', 'P&L %'].map(h => (
                        <TableCell
                          key={h}
                          align={h === 'Scrip' || h === 'Type' || h === 'Dir' ? 'left' : 'right'}
                          sx={{ bgcolor: '#FEF2F2', fontWeight: 700, fontSize: '0.6rem', color: COLORS.textSecondary, textTransform: 'uppercase', py: 0.8, px: 1, borderBottom: `2px solid #FCA5A5`, whiteSpace: 'nowrap' }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {uc.positions.map(p => {
                      const pnlColor = p.unrealizedPnL >= 0 ? COLORS.positive : COLORS.negative;
                      const isFutOrOpt = p.tradeType === TradeType.FUTURE || p.tradeType === TradeType.OPTION;
                      const pnlPct = p.investedAmount > 0 ? (p.unrealizedPnL / p.investedAmount) * 100 : 0;
                      return (
                        <TableRow key={p.key} sx={{ bgcolor: p.unrealizedPnL < 0 ? '#FEF2F2' : '#fff' }}>
                          <TableCell sx={{ px: 1, py: 0.8, fontSize: '0.75rem', fontWeight: 700 }}>{p.scrip}</TableCell>
                          <TableCell sx={{ px: 1, py: 0.8 }}>
                            <Chip label={getTradeTypeLabel(p.tradeType, p.tradeLegs)} size="small" sx={{ fontSize: '0.5rem', height: 18, fontWeight: 600 }} />
                          </TableCell>
                          <TableCell sx={{ px: 1, py: 0.8 }}>
                            <Chip label={p.direction} size="small" sx={{ fontSize: '0.5rem', height: 18, fontWeight: 700, bgcolor: p.direction === 'BUY' ? '#D1FAE5' : '#FEE2E2', color: p.direction === 'BUY' ? COLORS.positive : COLORS.negative }} />
                          </TableCell>
                          <TableCell align="right" sx={{ px: 1, py: 0.8, fontSize: '0.75rem' }}>{isFutOrOpt ? p.balanceLots : '-'}</TableCell>
                          <TableCell align="right" sx={{ px: 1, py: 0.8, fontSize: '0.75rem' }}>{p.shares.toLocaleString('en-IN')}</TableCell>
                          <TableCell align="right" sx={{ px: 1, py: 0.8, fontSize: '0.75rem' }}>{fmtCurrencyFull(p.weightedAvgBuyPrice)}</TableCell>
                          <TableCell align="right" sx={{ px: 1, py: 0.8, fontSize: '0.75rem', fontWeight: 700, color: COLORS.blue }}>{fmtCurrencyFull(p.currentPrice)}</TableCell>
                          <TableCell align="right" sx={{ px: 1, py: 0.8, fontSize: '0.75rem' }}>{fmtCurrency(p.investedAmount)}</TableCell>
                          <TableCell align="right" sx={{ px: 1, py: 0.8, fontSize: '0.75rem' }}>{fmtCurrency(p.currentValue)}</TableCell>
                          <TableCell align="right" sx={{ px: 1, py: 0.8, fontSize: '0.75rem', fontWeight: 700, color: pnlColor }}>{fmtCurrency(p.unrealizedPnL)}</TableCell>
                          <TableCell align="right" sx={{ px: 1, py: 0.8 }}>
                            <Chip
                              label={`${pnlPct >= 0 ? '+' : ''}${pnlPct.toFixed(2)}%`}
                              size="small"
                              sx={{ fontWeight: 700, fontSize: '0.6rem', height: 20, bgcolor: p.unrealizedPnL >= 0 ? COLORS.positiveLight : COLORS.negativeLight, color: pnlColor }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
}

export default RiskDashboard;
